import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { AUTH_FORCE_SIGNED_OUT_EVENT, isLogoutInProgress, signOut as authSignOut } from "@/lib/auth/authClient";

const LEGACY_FLAG = "ss2026_registered";

function mirrorLegacy(on: boolean) {
  if (typeof window === "undefined") return;
  try {
    if (on) window.localStorage.setItem(LEGACY_FLAG, "true");
    else {
      window.localStorage.removeItem(LEGACY_FLAG);
      window.localStorage.removeItem("ss2026_session_id");
    }
  } catch { /* noop */ }
}

/**
 * P0-AUTH-09 — Optimistic session read.
 *
 * Supabase persists the session as JSON in localStorage under the key
 * `sb-<project-ref>-auth-token`. On Android Chrome cold-start the auth
 * lock (`navigator.locks['lock:sb-…-auth-token']`) is regularly held by
 * the Service Worker / PWA shell for >3s, which made the original
 * `getSession()` race with our watchdog and resolve `session=null`.
 *
 * This function reads the same JSON synchronously (no lock involved) so
 * we can flip `authReady=true` with the cached session immediately, then
 * let `getSession()` confirm or correct it in the background.
 */
function readPersistedSession(): Session | null {
  if (typeof window === "undefined") return null;
  if (isLogoutInProgress()) return null;
  try {
    const ref = (import.meta.env.VITE_SUPABASE_PROJECT_ID as string | undefined) ?? "";
    if (!ref) return null;
    const raw = window.localStorage.getItem(`sb-${ref}-auth-token`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Session> & { expires_at?: number };
    if (!parsed?.access_token || !parsed?.refresh_token) return null;
    // Honor expiry — never resurrect a clearly expired session.
    if (typeof parsed.expires_at === "number" && parsed.expires_at * 1000 < Date.now()) {
      return null;
    }
    return parsed as Session;
  } catch {
    return null;
  }
}

export type AuthState = {
  isAuthenticated: boolean;
  isLoading: boolean;
  authReady: boolean;
  user: User | null;
  session: Session | null;
  login: () => void;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

/**
 * AuthProvider — P0-AUTH-07.
 *
 * SINGLE source of truth for the Supabase session across the app. Mounts
 * exactly ONE `onAuthStateChange` listener and ONE `getSession()` call.
 *
 * Why this exists: prior to P0-AUTH-07, `useAuth` was a per-instance
 * hook. Each consumer (Header, ProfileMenu, NationGate, FanTitleCard,
 * SocialPage, etc.) spawned its own `getSession()` + listener. On Android
 * Chrome, the resulting contention over `navigator.locks` +
 * `localStorage` left some instances with `authReady=false` forever,
 * freezing `/social` in an infinite spinner.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  // P0-AUTH-09 — seed state from localStorage so refresh on Android Chrome
  // never starts from a "no session" baseline that can leak into redirects.
  const initialOptimistic = readPersistedSession();
  const [session, setSession] = useState<Session | null>(initialOptimistic);
  const [user, setUser] = useState<User | null>(initialOptimistic?.user ?? null);
  const [isLoading, setIsLoading] = useState<boolean>(!initialOptimistic);
  // If we found a cached session, declare ready immediately. getSession()
  // will still run below and reconcile (refresh / invalidate) in background.
  const [authReady, setAuthReady] = useState<boolean>(!!initialOptimistic);
  const bootedRef = useRef(false);

  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;
    if (typeof console !== "undefined") {
      console.log("[AUTH_PROVIDER_BOOT]", { optimistic: !!initialOptimistic });
    }

    // Single source of truth for "the auth bootstrap finished hydrating".
    // `finish(s)` is called by WHICHEVER signal arrives first:
    //   (a) `getSession()` resolves with the stored session;
    //   (b) `onAuthStateChange` fires INITIAL_SESSION (Supabase always emits
    //       this on subscribe — even when offline);
    //   (c) the safety watchdog (last resort, only if everything stalls).
    // P0-AUTH-09: the watchdog must NEVER flip ready with `session=null`
    // while getSession() is still pending — that was the remaining race
    // that bounced authenticated users to /welcome on hard refresh in
    // Android Chrome. The reconciled version below:
    //   - seeds `latest` from the optimistic localStorage read,
    //   - tracks whether getSession() has resolved,
    //   - only lets the watchdog finish when it has a session OR
    //     getSession() has already returned null.
    let settled = false;
    let latest: Session | null = initialOptimistic;
    let getSessionResolved = false;
    const finish = (s: Session | null, reason: string) => {
      // Preserve any session we already saw; never let a later `null`
      // overwrite a real session captured by an earlier signal.
      if (s) latest = s;
      if (settled) return;
      settled = true;
      setSession(latest);
      setUser(latest?.user ?? null);
      mirrorLegacy(!!latest);
      setIsLoading(false);
      setAuthReady(true);
      if (typeof console !== "undefined") {
        console.log("[AUTH_PROVIDER_READY]", { hasSession: !!latest, reason });
      }
    };

    const forceSignedOut = () => {
      latest = null;
      settled = true;
      setSession(null);
      setUser(null);
      mirrorLegacy(false);
      setIsLoading(false);
      setAuthReady(true);
      console.log("[AUTH_PROVIDER_READY]", { hasSession: false, reason: "forced-signout" });
    };
    window.addEventListener(AUTH_FORCE_SIGNED_OUT_EVENT, forceSignedOut);

    // 1) Listener FIRST so we catch INITIAL_SESSION + any event during boot.
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      if (s) latest = s;
      setSession(s);
      setUser(s?.user ?? null);
      mirrorLegacy(!!s);
      // INITIAL_SESSION is the canonical "bootstrap done" signal from
      // Supabase. Use it to release the gate immediately.
      if (!settled && (event === "INITIAL_SESSION" || event === "SIGNED_IN" || event === "SIGNED_OUT")) {
        finish(s, `event:${event}`);
      }
    });

    // 2) Hydrate existing session in parallel.
    supabase.auth.getSession()
      .then(({ data }) => { getSessionResolved = true; finish(data.session, "getSession"); })
      .catch(() => { getSessionResolved = true; finish(null, "getSession:error"); });

    // 3) Last-resort watchdog. Two-phase to avoid the Android race:
    //    - At 3s: only release if we already have a session candidate
    //      (optimistic read or live event). Never publish `null` while
    //      getSession() is still pending — that's what bounced signed-in
    //      users to /welcome.
    //    - At 8s: absolute safety net. If everything is still stalled,
    //      give up and release whatever we have (likely null) so the UI
    //      can at least show the login flow instead of freezing.
    const watchdog = setTimeout(() => {
      if (latest || getSessionResolved) finish(latest, "watchdog");
    }, 3000);
    const hardWatchdog = setTimeout(() => finish(latest, "watchdog:hard"), 8000);

    return () => {
      clearTimeout(watchdog);
      clearTimeout(hardWatchdog);
      window.removeEventListener(AUTH_FORCE_SIGNED_OUT_EVENT, forceSignedOut);
      sub.subscription.unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<AuthState>(() => ({
    isAuthenticated: !!session,
    isLoading,
    authReady,
    user,
    session,
    login: () => { mirrorLegacy(true); },
    logout: () => authSignOut(),
  }), [session, user, isLoading, authReady]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * useAuth — consumes the global AuthProvider context. The API is
 * backwards-compatible with the previous per-instance hook so the 14
 * existing call sites work unchanged.
 */
export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (ctx) return ctx;
  // Fallback (should never happen in app code — AuthProvider is mounted
  // in __root.tsx). Returns a safe "not ready" state so SSR / isolated
  // tests never crash.
  return {
    isAuthenticated: false,
    isLoading: true,
    authReady: false,
    user: null,
    session: null,
    login: () => {},
    logout: async () => {},
  };
}
